import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import customParseFormat from 'dayjs/plugin/customParseFormat'

// Extend dayjs with required plugins
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)

/**
 * Parses ID3 Tag date strings using multiple format patterns with Europe/Dublin timezone
 * Based on TG4 iOS and Android implementations
 *
 * @param dateString - The date string to parse (from start_date_time or ref_date fields)
 * @returns Parsed Date object or undefined if parsing fails
 */
export function formatTimeForID3Tag(dateString: string | undefined): Date | undefined {
  if (!dateString) {
    return undefined
  }

  // Date formats to try in order (matching iOS/Android implementations)
  const dateFormats: string[] = [
    'YYYY-MM-DD HH:mm:ss.SSS',      // Primary format with milliseconds
    'YYYY-MM-DDTHH:mm:ss.SSS',      // ISO-style with milliseconds
    'YYYY-MM-DD HH:mm:ss.SS',       // Centisecond precision
    'YYYY-MM-DDTHH:mm:ss.SS',       // ISO-style with centiseconds
    'YYYY-MM-DD'                    // Date only fallback
  ]

  // Try each format with Europe/Dublin timezone
  for (const format of dateFormats) {
    const parsedDate = dayjs.tz(dateString, format, 'Europe/Dublin')
    if (parsedDate.isValid()) {
      return parsedDate.toDate()
    }
  }

  // Fallback to ISO8601 parsing (in case tags are not actually Irish TZ)
  const isoDate = dayjs(dateString)
  if (isoDate.isValid()) {
    console.warn('Date parsed using ISO8601 fallback:', dateString)
    return isoDate.toDate()
  }

  console.error('No format found for date parsing:', dateString)
  return undefined
}

/**
 * Formats a Date object for Nielsen ingestion
 * Nielsen expects UTC dates in "YYYYMMDD HH:mm:ss" format
 *
 * @param date - The Date object to format
 * @returns Formatted string for Nielsen
 */
export function formatDateForNielsen(date: Date): string {
  return dayjs(date).utc().format('YYYYMMDD HH:mm:ss')
}

export function formatLongRunningDate(dateString: string | undefined | null): string | undefined {
  if (!dateString) {
    return undefined
  }

  const date = dayjs(dateString)
  return date.isValid() ? date.format('DD-MM-YYYY') : undefined
}
